// This is a very simple tokenizer with a primary aim of parsing very simple SVG files
// to verify that those are sanitized afterwards

// It's minimalistic by a design goal. Supporting all the XML/SVG files out there is not a goal

const assert = (ok, message = 'assertion') => {
  if (!ok) throw new Error(message)
}

function tokenizeFile(raw) {
  // Splits file into xml tags or text content
  assert(typeof raw === 'string')
  const tokens = []
  let i = 0
  while (i < raw.length) {
    let j = raw.indexOf('<', i)
    if (j === i) {
      j = raw.indexOf('>', i)
      assert(j > i, '> expected')
      j++
    } else if (j === -1) {
      j = raw.length
    }
    tokens.push(raw.slice(i, j))
    i = j
  }
  // Verify that we missed no characters
  assert(tokens.join('') === raw)
  return tokens
}

function tokenizeLine(raw) {
  // Splits file into xml tags or text content
  assert(typeof raw === 'string')
  assert(raw.startsWith('<') && raw.endsWith('>'))
  let i = raw.indexOf(' ')
  if (i < 0) i = raw.length - 1
  const tokens = []
  tokens.push(raw.slice(1, i))
  while (i < raw.length - 1) {
    let j = raw.indexOf('"', i)
    if (j === -1) {
      j = raw.length - 1
      if (i !== j) tokens.push(raw.slice(i, j))
      i = j
    } else {
      assert(j > i)
      const k = raw.indexOf('"', j + 1)
      assert(k > j)
      tokens.push(raw.slice(i, k + 1))
      i = k + 1
    }
  }
  // Verify that we missed no characters
  assert(`<${tokens.join('')}>` === raw)
  return tokens
}

/*
   (\.\d+|\d+(\.\d*)?)      -- non-negative numeric
  -(\.\d+|\d+(\.\d*)?)      -- maybe-negative numeric
   (#[a-fA-F0-9]+|[a-z]+)   -- color
*/

function isTransform(value) {
  return /^((translate|rotate|scale|matrix)\(-?(\.\d+|\d+(\.\d*)?)((, *| |)-?(\.\d+|\d+(\.\d*)?)){0,5}\) *){1,6}$/.test(
    value
  )
}

// Note: tag -> option map is not ideal (e.g. linearGradient/radialGradient is loose),
// but it is kept that way for clarity of the code
// The main intention is to block unknown tags / values
function validateOption(tag, option) {
  if (/^id="(Слой)?[a-zA-Z0-9_:-]+"$/.test(option)) return
  if (['path', 'stop', 'circle', 'ellipse', 'rect'].includes(tag) && option === '/') return
  if (tag === '?xml' && option === '?') return

  assert(/^[a-z0-9:-]+="[a-z0-9_,.:;#%/() -]+"$/i.test(option)) // Enforce general form

  const [name, value] = option.split(/=?"/)
  assert(option === `${name}="${value}"`) // Verify that we missed no characters

  if (tag === 'svg') {
    if (option === 'xmlns="http://www.w3.org/2000/svg"') return
    if (option === 'xmlns:xlink="http://www.w3.org/1999/xlink"') return
    if (/^version="\d.\d"$/.test(option)) return
    if (/^(width|height)="(\.\d+|\d+(\.\d*)?)(%|px)?"$/.test(option)) return
    if (/^viewBox="(\.\d+|\d+(\.\d*)?)(( |, *)(\.\d+|\d+(\.\d*)?)){3}"$/.test(option)) return
    if (/^data-name="Layer \d+"$/.test(option)) return
    if (/^style="enable-background:new 0 0( (\.\d+|\d+(\.\d*)?)){2};"$/.test(option)) return
    if (/^(x|y)="0(px)?"$/.test(option)) return
    if (/^xml:space="preserve"$/.test(option)) return
  }
  if (['path', 'g', 'circle', 'ellipse', 'rect', 'mask', 'svg'].includes(tag)) {
    if (/^(fill|clip)-rule="(nonzero|evenodd)"$/.test(option)) return
    if (/^stroke-(linejoin|linecap)="round"$/.test(option)) return
    if (/^(stroke-(width|miterlimit)|(fill-)?opacity)="(\.\d+|\d+(\.\d*)?)"$/.test(option)) return
    if (/^(fill|stroke)="(#[a-fA-F0-9]+|[a-z]+)"$/.test(option)) return
    if (/^style="fill:(#[a-fA-F0-9]+|[a-z]+);"$/.test(option)) return
    if (name === 'transform' && isTransform(value)) return
  }
  if (['path', 'g', 'circle', 'ellipse', 'rect'].includes(tag)) {
    if (/^(fill|stroke|mask|filter|clip-path)="url\(#[a-zA-Z0-9_:-]+\)"$/.test(option)) return
  }
  if (tag === 'stop') {
    if (/^offset="(\.\d+|\d+(\.\d*)?)%?"$/.test(option)) return
    if (/^stop-color="(#[a-fA-F0-9]+|[a-z]+)"$/.test(option)) return
    if (/^stop-opacity="(\.\d+|\d+(\.\d*)?)"$/.test(option)) return
  }
  if (['linearGradient', 'radialGradient'].includes(tag)) {
    if (/^(x1|x2|y1|y2|fx|fy)="-?(\.\d+|\d+(\.\d*)?)(e-\d+)?%?"$/.test(option)) return
    if (/^gradientUnits="(userSpaceOnUse|objectBoundingBox)"$/.test(option)) return
    if (name === 'gradientTransform' && isTransform(value)) return
  }
  if (['circle', 'ellipse', 'radialGradient'].includes(tag)) {
    if (/^(cx|cy|rx|ry|r)="(\.\d+|\d+(\.\d*)?)"$/.test(option)) return
  }
  if (tag === 'pattern') {
    if (/^patternContentUnits="(objectBoundingBox)"$/.test(option)) return
  }
  if (tag === 'mask') {
    if (/^mask-type="(alpha)"$/.test(option)) return
    if (/^maskUnits="(userSpaceOnUse)"$/.test(option)) return
    if (/^style="mask-type:alpha"$/.test(option)) return
  }
  if (['mask', 'rect', 'pattern'].includes(tag)) {
    if (/^(width|height|rx)="(\.\d+|\d+(\.\d*)?)"$/.test(option)) return
    if (/^(x|y)="-?(\.\d+|\d+(\.\d*)?)"$/.test(option)) return
  }
  if (tag === 'path' && name === 'd') {
    assert(value.length <= 10000, '<path> too long')
    if (/^[\d\-.e, LlHhVvZzCcSsQqTtAaMm]+$/.test(value)) return
  }
  if (tag === '?xml') {
    if (['version="1.0"', '?', 'standalone="no"'].includes(option)) return
    if (/^encoding="(utf-8|UTF-8|iso-8859-1|ISO-8859-1)"$/.test(option)) return
  }
  assert(false, 'unexpected svg element')
}

const tags = [
  ...['svg', 'defs'],
  ...['linearGradient', 'radialGradient', 'stop'],
  ...['g', 'path', 'circle', 'ellipse', 'rect'],
  ...['clipPath', 'mask', 'pattern'],
]
const allTags = ['?xml', ...tags, ...tags.map((t) => `/${t}`), ...tags.map((t) => `${t}/`)]

export function validate(raw) {
  for (const token of tokenizeFile(raw)) {
    if (token.trim() === '') continue
    const [tag, ...options] = tokenizeLine(token)
    assert(allTags.includes(tag))
    if (tag.startsWith('/')) assert(options.length === 0)
    for (const option of options) validateOption(tag, option.replace(/^ +/, ''))
  }
}
